import React from "react";
import * as Icons from "lucide-react";

/**
 * IntegrationIcon
 * Renders a monochrome brand-tinted icon for a mock 3rd-party integration.
 * The icon uses the integration's `brand_color` (dark→white text, light→dark text)
 * so the little chip always reads well.
 *
 * Falls back to the first letter of the integration name if the icon is missing.
 */
function isDarkHex(hex) {
    if (!hex || typeof hex !== "string") return false;
    const c = hex.replace("#", "");
    if (c.length !== 6) return false;
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    // relative luminance
    const L = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return L < 0.55;
}

const SIZES = {
    xs: "w-6 h-6",
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
};
const INNER = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
};
const RADIUS = {
    xs: "rounded-md",
    sm: "rounded-lg",
    md: "rounded-lg",
    lg: "rounded-xl",
};

export default function IntegrationIcon({
    integration,
    size = "md",
    withRing = false,
    className = "",
    testid,
}) {
    if (!integration) return null;
    const IconCmp = Icons[integration.icon] || Icons.Plug;
    const bg = integration.brand_color || "#0f172a";
    const dark = isDarkHex(bg);
    const fg = dark ? "#ffffff" : "#0f172a";
    const ring = withRing ? "ring-2 ring-white shadow-sm" : "";
    return (
        <div
            data-testid={testid || `int-icon-${integration.id}`}
            title={integration.name}
            className={`${SIZES[size]} ${RADIUS[size]} grid place-items-center flex-shrink-0 ${ring} ${className}`}
            style={{ backgroundColor: bg, color: fg }}
        >
            <IconCmp className={INNER[size]} strokeWidth={2} />
        </div>
    );
}
